import { Howl, Howler } from 'howler';
import i18n from '@/i18n';
import store from '@/store';

import enMp3 from '@/assets/audio/en/en.mp3';
import enMeta from '@/assets/audio/en/en.json';
import deMp3 from '@/assets/audio/de/de.mp3';
import deMeta from '@/assets/audio/de/de.json';
import frMp3 from '@/assets/audio/fr/fr.mp3';
import frMeta from '@/assets/audio/fr/fr.json';
import esMp3 from '@/assets/audio/es/es.mp3';
import esMeta from '@/assets/audio/es/es.json';
import jaMp3 from '@/assets/audio/ja/ja.mp3';
import jaMeta from '@/assets/audio/ja/ja.json';
import zhMp3 from '@/assets/audio/zh-tw/zh-tw.mp3';
import zhMeta from '@/assets/audio/zh-tw/zh-tw.json';
import trMp3 from '@/assets/audio/tr/tr.mp3';
import trMeta from '@/assets/audio/tr/tr.json';
import koMp3 from '@/assets/audio/ko/ko.mp3';
import koMeta from '@/assets/audio/ko/ko.json';
import itMp3 from '@/assets/audio/it/it.mp3';
import itMeta from '@/assets/audio/it/it.json';
import ptMp3 from '@/assets/audio/pt/pt.mp3';
import ptMeta from '@/assets/audio/pt/pt.json';
import roMp3 from '@/assets/audio/ro/ro.mp3';
import roMeta from '@/assets/audio/ro/ro.json';

import stampBgmSource from '@/assets/stamp/audio/bgm.mp3';
import stampMp3 from '@/assets/stamp/audio/sprites.mp3';
import stampMeta from '@/assets/stamp/audio/sprites.json';

const RESOURCES = {
  en: { src: enMp3, sprite: enMeta.sprite },
  fr: { src: frMp3, sprite: frMeta.sprite },
  es: { src: esMp3, sprite: esMeta.sprite },
  de: { src: deMp3, sprite: deMeta.sprite },
  ja: { src: jaMp3, sprite: jaMeta.sprite },
  tr: { src: trMp3, sprite: trMeta.sprite },
  ko: { src: koMp3, sprite: koMeta.sprite },
  it: { src: itMp3, sprite: itMeta.sprite },
  pt: { src: ptMp3, sprite: ptMeta.sprite },
  ro: { src: roMp3, sprite: roMeta.sprite },
  'zh-tw': { src: zhMp3, sprite: zhMeta.sprite },
  'zh-cn': { src: zhMp3, sprite: zhMeta.sprite },
  stamp: { src: stampMp3, sprite: stampMeta.sprite },
};

const AudioManager = {
  bgm: null,
  isLoading: false,
  loadAllAudio(mode) {
    const isStamp = store.getters['app/isStamp'];
    let target = isStamp ? 'stamp' : mode;
    // fallback to locale if audio for specified mode is not there
    target = RESOURCES[target] ? target : i18n.locale;
    console.log('AudioManager.loadAllAudio', mode, target);
    this.isLoading = true;
    if (!RESOURCES[target] && target.includes('-')) {
      const baseLang = target.split('-')[0];
      target = RESOURCES[baseLang] ? baseLang : i18n.locale;
    }
    if (!RESOURCES[target]) {
      target = 'en';
      console.log(`audio for ${mode} and ${i18n.locale} not found. falling back to en.`);
    }
    const { src, sprite } = RESOURCES[target];
    if (!src || !sprite) {
      this.isLoading = false;
      return Promise.reject(new Error('Invalid mode given'));
    }

    // stop and free the pack loaded before (a locale switch replaces it)
    if (window.CloudzetAudio && typeof window.CloudzetAudio.unload === 'function') {
      window.CloudzetAudio.unload();
    }

    return new Promise((resolve, reject) => {
      window.CloudzetAudio = new Howl({
        src,
        sprite,
        html5: true,
        onload: () => {
          console.log('Howler.ctx.sampleRate', Howler.ctx.sampleRate);
          console.log('Audio loaded', target);
          if (!isStamp) {
            this.isLoading = false;
            resolve();
          }
        },
        onloaderror: (id, error) => {
          console.log('Unable to load audio', id, error, mode);
          this.isLoading = false;
          reject();
        },
        onplayerror: (id, error) => {
          if (error.includes('playback was not within a user interaction')) {
            console.log('[WARNING] Unable to play sound: ', id, error, mode);
            return;
          }
          console.log('[ERROR] Unable to play sound: ', id, error, mode);
        },
      });

      window.CloudzetAudio.has = key => {
        return sprite[key] != null;
      };
      window.CloudzetAudio.getTime = key => {
        if (Array.isArray(sprite[key]) && sprite[key].length > 1) {
          return sprite[key][1];
        }
        return 0;
      };

      if (isStamp) {
        if (this.bgm) {
          this.isLoading = false;
          resolve();
          return;
        }

        this.bgm = new Howl({
          src: [stampBgmSource],
          autoplay: true,
          loop: true,
          onload: () => {
            console.log('stamp BGM_LOADED');
            this.isLoading = false;
            resolve();
          },
          onloaderror: (id, error) => {
            console.log('Unable to load stamp bgm', id, error);
            this.isLoading = false;
            reject();
          },
          onplayerror: (id, error) => {
            console.log('[ERROR] Unable to play bgm: ', id, error);
            // try play bgm if the error is due to browser locking audio
            this.bgm.once('unlock', () => {
              this.bgm.play();
            });
          },
        });
      }
    });
  },
  waitForLoadEnd() {
    return new Promise(resolve => {
      const checkAgain = () => {
        if (this.isLoading) {
          setTimeout(checkAgain, 1000);
        } else {
          resolve();
        }
      };
      checkAgain();
    });
  },
  unloadAudio() {
    if (this.bgm && typeof this.bgm.unload === 'function') {
      this.bgm.unload();
    }
    this.bgm = null;
    if (window.CloudzetAudio && typeof window.CloudzetAudio.unload === 'function') {
      window.CloudzetAudio.unload();
    }
    window.CloudzetAudio = null;
  },
};

export default AudioManager;
